import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { FaFolderOpen } from "react-icons/fa";
import FileInventoryExplorer from "@/pages/Admin/SharePointInventory/FileInventoryExplorer";
import ScanStateBadge from "@/pages/Admin/SharePointInventory/ScanStateBadge";
import { formatBytes, formatCount, formatDate } from "@/pages/Admin/SharePointInventory/format";
import {
  createResourceFileInventoryDataSource,
  getResourceSiteInventory,
  type SiteInventoryDetailDto,
} from "@/api/sharepointInventoryApi";

interface Props {
  groupId: string;
}

const describeError = (err: unknown): string => {
  const response = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
  const status = response?.status;
  const message = response?.data?.error;
  if (status === 403) return message || "Only owners of this group can view its file inventory.";
  if (status === 404) return message || "No file inventory is available for this site yet.";
  return message || "Failed to load the file inventory. Please try again.";
};

/** Site-scoped file inventory for My Resources. Nothing is requested until the user asks for it. */
const ResourceFileInventory: React.FC<Props> = ({ groupId }) => {
  const [detail, setDetail] = useState<SiteInventoryDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dataSource = useMemo(() => createResourceFileInventoryDataSource(groupId), [groupId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await getResourceSiteInventory(groupId));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!detail) {
    return (
      <div className="py-3">
        <p className="text-muted mb-3">
          Review the files in this site&apos;s document libraries from the most recent inventory scan: search,
          filter by type, age or size, and export to CSV. This shows stored scan results and does not start a new scan.
        </p>
        {error && <Alert variant="warning">{error}</Alert>}
        <Button variant="primary" onClick={load} disabled={loading}>
          {loading ? (
            <><Spinner size="sm" animation="border" className="me-2" />Loading file inventory...</>
          ) : (
            <><FaFolderOpen className="me-2" />Load file inventory</>
          )}
        </Button>
      </div>
    );
  }

  const { site, libraries } = detail;
  const pendingLibraries = libraries.filter((l) => l.status === "Pending").length;
  const hasInventory = Boolean(site.lastCompletedCrawlDt) || site.fileCount > 0;
  const exportPrefix = (site.title || "site").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "site";

  return (
    <div className="py-2">
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Scan state</div>
            <div className="fs-5"><ScanStateBadge state={site.scanState} /></div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Inventoried files</div>
            <div className="fs-4">{formatCount(site.fileCount)}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Inventoried storage</div>
            <div className="fs-4">{formatBytes(site.totalBytes)}</div>
          </Card>
        </Col>
        <Col md={3}>
          <Card body>
            <div className="text-muted small">Last completed scan</div>
            <div className="fs-5">{formatDate(site.lastCompletedCrawlDt)}</div>
          </Card>
        </Col>
      </Row>

      {!hasInventory && (
        <Alert variant="info">This site has not completed an inventory scan yet, so no files are available.</Alert>
      )}
      {site.scanState === "Stale" && (
        <Alert variant="warning">
          This inventory is {site.crawlAgeDays} days old. Recent changes to files may not be reflected.
        </Alert>
      )}
      {pendingLibraries > 0 && (
        <Alert variant="warning">
          {pendingLibraries} librar{pendingLibraries === 1 ? "y was" : "ies were"} not scanned in the latest completed
          scan. File counts for this site are incomplete.
        </Alert>
      )}

      {hasInventory && (
        <FileInventoryExplorer
          dataSource={dataSource}
          showSiteColumn={false}
          exportFileNamePrefix={`${exportPrefix}-files`}
        />
      )}
    </div>
  );
};

export default ResourceFileInventory;
